import { useTranslation } from "react-i18next";
import { resolveEntityKeyLabel, useGlobalConfig } from "../../../Hooks/usePagesContent";

// Entity Filter tabs whose value is a vehicle identifier (shown as-is).
export const VEHICLE_ID_ENTITY_TYPES = ["licensePlateNo", "vin"];

// Entity Filter tabs whose value is a battery identifier other than the device ID (shown as-is).
export const BATTERY_ID_ENTITY_TYPES = ["customerBatteryID"];

export const CHASSIS_NUMBER_FALLBACK = "Chassis Number";
export const SERIAL_ID_FALLBACK = "Serial ID";

/**
 * Label + value for the Advanced Analysis entity ID box, matching the Entity
 * Filter tab the entity was picked from:
 *   licensePlateNo -> baseEntity.idLabel        (fallback "Bus Number")
 *   vin            -> searchableFields vin title (fallback "Chassis Number")
 *   customerBatteryID -> searchableFields customerBatteryID title (fallback "Serial ID")
 *   anything else  -> searchableFields deviceID title (fallback "Device ID"),
 *                     value = the searched device ID, else the battery's device ID.
 *                     Never the internal battery ID.
 * `t` is i18next's translate function; it supplies today's fallback copy.
 */
export const resolveEntityIdDisplay = ({ selectedEntity, batteryInfo, globalConfig, t }) => {
    const translate = typeof t === "function" ? t : (key) => key;
    const type = selectedEntity?.type;
    if (VEHICLE_ID_ENTITY_TYPES.includes(type)) {
        const fallback = type === "vin" ? CHASSIS_NUMBER_FALLBACK : translate("digital_twin:section_ids.bus_number");
        return {
            isVehicleId: true,
            label: resolveEntityKeyLabel(globalConfig, type, fallback),
            value: selectedEntity?.value || "N/A",
        };
    }
    if (BATTERY_ID_ENTITY_TYPES.includes(type)) {
        return {
            isVehicleId: false,
            label: resolveEntityKeyLabel(globalConfig, type, SERIAL_ID_FALLBACK),
            value: selectedEntity?.value || "N/A",
        };
    }
    return {
        isVehicleId: false,
        label: resolveEntityKeyLabel(globalConfig, "deviceID", translate("common:device_id")),
        // A device ID search already holds the IMEI; batteryInfo can still be the
        // previous battery's until the new real-time info arrives.
        value: (selectedEntity?.type === "deviceID" ? selectedEntity?.value : null)
            || batteryInfo?.deviceID
            || "N/A",
    };
};

/** resolveEntityIdDisplay for the page in context. */
export const useEntityIdDisplay = (selectedEntity, batteryInfo) => {
    const { t } = useTranslation(["common", "digital_twin"]);
    const globalConfig = useGlobalConfig();
    return resolveEntityIdDisplay({ selectedEntity, batteryInfo, globalConfig, t });
};
